import { useDrawer } from '@src/components/Drawer';
import { useCallbackRef } from '@src/hooks/useCallbackRef';
import { AppwideDrawerNavigationBehavior } from '@src/providers/AppwideDrawerProvider/AppwideDrawerNavigationBehavior';
import { useCallback, useState, type PropsWithChildren, type ReactNode } from 'react';
import type { AppwideDrawerContentType } from './AppwideDrawerContentType';
import { AppwideDrawerContext } from './useAppwideDrawerContext';

export interface AppwideDrawerContentSpec {
  type: AppwideDrawerContentType;
  /** Optional owner token used by shared drawer consumers to avoid closing content they do not own. */
  ownerId?: string;
  title?: string;
  /** Content renders its own header instead of the shared appwide header. */
  hideHeader?: boolean;
  /** when drawerHeaderDesign is 'flex' and this is provided, this will be used to render the header content */
  flexHeaderTitle?: () => ReactNode;
  renderContent: () => ReactNode;
  renderHeaderContent?: () => ReactNode;
  /** Width of drawer, defaults to 450 */
  width?: number;
  /** CSS Position of drawer, defaults to relative. Determines if drawer is opened next to content ('relative') or on top of content ('absolute') */
  position?: 'relative' | 'absolute';
  placement?: 'right' | 'left';
  /** The type of header to use: 'tab' for tab-based header, 'flex' for simple flex header, defaults to 'tab' */
  drawerHeaderDesign?: 'tab' | 'flex';
  /** Close handler for the header button and Escape. Defaults to the appwide drawer close. Can veto closure by not calling close. */
  onClose?: () => void;
  /** When true, pathname changes do not close this drawer. */
  persistOnNavigate?: boolean;
}

/**
 * Controlled AppWideDrawer provider.
 *
 * Use this when the app owns guide visibility state, especially when the value
 * must be persisted in user settings or synchronized with another app-level
 * store. Apps that only need session-local guide state should use
 * AppwideDrawerProviderWithLocalGuideState instead.
 */
export interface AppwideDrawerProviderProps extends PropsWithChildren {
  isGuideVisible: boolean;
  setIsGuideVisible: (visible: boolean) => void;
}

export const AppwideDrawerProvider = function AppwideDrawerProvider({
  children,
  isGuideVisible,
  setIsGuideVisible,
}: AppwideDrawerProviderProps) {
  const [contentSpec, setContentSpec] = useState<AppwideDrawerContentSpec | undefined>(undefined);

  const drawer = useDrawer({
    closeOnEscape: false,
    width: contentSpec?.width ?? 450,
    position: contentSpec?.position ?? 'relative',
    placement: contentSpec?.placement,
  });

  // useCallbackRef: drawer from useDrawer may change identity; keep openDrawer stable for context consumers.
  const openDrawer = useCallbackRef((spec: AppwideDrawerContentSpec) => {
    setContentSpec(spec);
    drawer.open();
  });

  const toggleGuide = useCallback(() => {
    setIsGuideVisible(!isGuideVisible);
  }, [setIsGuideVisible, isGuideVisible]);

  const resetGuide = useCallback(() => setIsGuideVisible(false), [setIsGuideVisible]);

  return (
    <AppwideDrawerContext.Provider
      value={{
        drawer,
        openDrawer,
        closeDrawer: drawer.close,
        contentSpec,
        isOpen: drawer.isOpen,
        isGuideVisible,
        toggleGuide,
        resetGuide,
      }}
    >
      <AppwideDrawerNavigationBehavior drawer={drawer} contentSpec={contentSpec} />

      {children}
    </AppwideDrawerContext.Provider>
  );
};

/**
 * Session-local AppWideDrawer provider.
 *
 * Use this for consumers that do not have a persisted app setting for guide
 * visibility. The guide state is kept only while this provider is mounted.
 */
export function AppwideDrawerProviderWithLocalGuideState({ children }: PropsWithChildren) {
  const [isGuideVisible, setIsGuideVisible] = useState(false);

  return (
    <AppwideDrawerProvider isGuideVisible={isGuideVisible} setIsGuideVisible={setIsGuideVisible}>
      {children}
    </AppwideDrawerProvider>
  );
}
